import axios from 'axios';
import Cookies from "js-cookie";
import moment from 'moment';
import { withApiError, withApiErrorResponse, apiErrorMessage } from "../utils/apiError";

const baseURL = process.env.REACT_APP_BASE_BACKEND_API_URL;

/**
 * Create or update a protected account.
 * @param {Object} protectedAcc - The protected account data.
 * @param {string} accessToken - The access token for authentication.
 * @returns {Object} The response data from the API.
 */
export const createProtectedAccount = async (protectedAcc, accessToken) => {
  try {
    const response = await axios.post(
      `${baseURL}api/v1/account-protection/create-or-update`,
      protectedAcc,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    return withApiError(response.data);
  } catch (error) {
    console.error('Error creating protected account:', error);
    throw withApiErrorResponse(error);
  }
};

/**
 * Fetch all protected accounts.
 * @param {string} accessToken - The access token for authentication.
 * @param {number} page - The page number for pagination.
 * @param {number} size - The number of items per page.
 * @param {string} workflowId - The workflow ID.
 * @returns {Object} The response data from the API.
 */
export const fetchProtectedAccounts = async (accessToken, page, size, selectedOrgId,accountNo, projectId) => {
  try {
    const response = await axios.get(
      `${baseURL}api/v1/account-protection/getAll`, 
      {
        params: {
          page: page,
          size: size,
          organizationId: selectedOrgId,
          accountNo,
          projectId
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response || !response.data || response.data.header.code !== 600) {
      throw new Error('Failed to fetch protected accounts');
    }

    return response.data.body.value;
  } catch (error) {
    console.error('Error fetching protected accounts:', error);
    throw withApiErrorResponse(error);
  }
};

/**
 * Search protected accounts with server-side column filters.
 * @param {Object} body - { projectId, page, size, search, columnFilters }.
 * @param {AbortSignal} [signal] - Cancels a superseded request.
 * @returns {Object} { accountProtectionList, totalCount }.
 */
export const searchProtectedAccounts = async (body, signal) => {
  const accessToken = Cookies.get("accessToken");
  const response = await axios.post(
    `${baseURL}api/v1/account-protection/search`,
    body,
    {
      signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response || !response.data || response.data.header?.code !== 600) {
    throw new Error('Failed to search protected accounts');
  }

  return response.data.body.value;
};

/**
 * Distinct values of one column, narrowed by the other active filters.
 * @param {Object} body - { projectId, column, search, limit, columnConditions }.
 * @param {AbortSignal} [signal] - Cancels a superseded request.
 * @returns {string[]} Values; "" stands for blank.
 */
export const fetchProtectionColumnValues = async (body, signal) => {
  const accessToken = Cookies.get("accessToken");
  const response = await axios.post(
    `${baseURL}api/v1/account-protection/column-values`,
    body,
    {
      signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response || !response.data || response.data.header?.code !== 600) {
    throw new Error('Failed to fetch column values');
  }

  return response.data.body.value || [];
};

/**
 * Fetch a particular protected account.
 * @param {string} accessToken - The access token for authentication.
 * @param {string} editUserId - The ID of the account to be edited.
 * @returns {Object} The response data from the API.
 */
export const fetchParticularAccount = async (accessToken, editUserId) => {
  try {
    const response = await axios.get(
      `${baseURL}api/v1/account-protection/get`, 
      {
        params: {
          id: editUserId,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response || !response.data || response.data.header.code !== 600) {
      throw new Error('Failed to fetch protected account');
    }

    return response.data.body.value;
  } catch (error) {
    console.error('Error fetching protected account:', error);
    throw withApiErrorResponse(error);
  }
};

/**
 * Delete selected protected accounts.
 * @param {Array} selectedRecords - The IDs of the accounts to be deleted.
 * @returns {Object} The response data from the API.
 */
export const deleteProtectedAccounts = async (selectedRecords) => {
  const token = Cookies.get("accessToken");  
  try {
    const response = await axios.delete(`${baseURL}api/v1/account-protection/delete`, {
      data: selectedRecords,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return withApiError(response.data);
  } catch (error) {
    console.error('Error deleting protected accounts:', error);
    throw withApiErrorResponse(error);
  }
};

/**
 * Export a report of protected accounts.
 * @param {string} selectedOrgId - The selected workflow ID.
 * @param {string} startDate - The start date for the report.
 * @param {string} endDate - The end date for the report.
 * @returns {Object} The response data from the API.
 */
export const fetchExportProtected = async (projectId, startDate, endDate ) => {
  const token = Cookies.get("accessToken");

  const formattedStartDate = moment(startDate).format("YYYY-MM-DDTHH:mm:ss");
  const formattedEndDate = moment(endDate).format("YYYY-MM-DDTHH:mm:ss");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/account-protection/generate-report`,
      {
        params: {
          projectId,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
         
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error('Error exporting report:', error);
    throw withApiErrorResponse(error);
  }
};

/**
  Fetch protection jobs for a given project with pagination.
 * @param {number|string} projectId - The project whose jobs are listed.
 * @param {{page?: number, size?: number}} paging - Zero-based page and page size.
 * @returns {{jobs: Array, totalCount: number}}
 */
export const fetchProtectionJobs = async (projectId, { page = 0, size = 10 } = {}) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/account-protection/job-status`,
      {
        params: { projectId, page, size },
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const data = withApiError(response.data);
    if (data?.header?.code !== 600) {
      throw new Error(apiErrorMessage(data, "Failed to fetch protection jobs"));
    }
    const value = data?.body?.value;
    const jobs = Array.isArray(value?.accountProtectionJobList) ? value.accountProtectionJobList : [];
    return { jobs, totalCount: Number(value?.totalCount) || 0 };
  } catch (error) {
    console.error("Error fetching protection jobs:", error);
    throw withApiErrorResponse(error);
  }
};
